/**
 * Shared utilities for Quote Job Additional (create/edit).
 * Group6 as single accordion array (no category tabs).
 */
import { getDefaultCostData, getGroup6SectionIdPrefixForAccordionItem, getGroup6CategoryFromSectionId, stripGroup6SectionIdPrefix, } from "@/features/admin/cost-parameters/costParametersDefaults";
import { calculateTotalCost } from "@/utils/costCalculations";
const FORMULA_SECTION_IDS = ["floor-finishes", "screeds", "grinding"];
function parseCurrency(value) {
    if (!value)
        return 0;
    const cleaned = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned) || 0;
}
/**
 * Get After Hours status and multiplier from the same section (for group6 area-based formulas).
 */
function getSectionAfterHoursForGroup6(section) {
    const afterHoursItem = section.items.find((i) => i.title.toLowerCase().includes("after hours"));
    if (!afterHoursItem?.inScope) {
        return { enabled: false, multiplier: 1 };
    }
    const mult = parseCurrency(afterHoursItem.unitCost);
    return {
        enabled: true,
        multiplier: mult > 0 ? mult : 1.3,
    };
}
/**
 * Compute total cost for a single group6 line item. Used to auto-fill totalCost when unitCost, qty, or areaInfo change.
 * Returns 0 if not in-scope or cannot calculate. Callers should format the result with formatCurrency() for display,
 * including when the result is 0 (so in-scope items always show a formatted total, e.g. "$0").
 */
export function computeGroup6ItemTotalCost(subItem, section, _accordionItem, areaInfo, _accordionData) {
    if (!subItem.inScope)
        return 0;
    const unitCostNum = parseCurrency(subItem.unitCost);
    const areaInfoForCalc = areaInfo.map((a) => ({
        label: a.label,
        value: a.value,
    }));
    if (FORMULA_SECTION_IDS.includes(section.sectionId)) {
        // After Hours items are toggle-only multipliers — they do not contribute a standalone cost
        if (subItem.title.toLowerCase().includes("after hours"))
            return 0;
        if (unitCostNum <= 0)
            return 0;
        const { enabled: afterHoursEnabled, multiplier: afterHoursMultiplier } = getSectionAfterHoursForGroup6(section);
        const result = calculateTotalCost({
            sectionId: section.sectionId,
            title: subItem.title,
            unitCost: unitCostNum,
            areaInfo: areaInfoForCalc,
            afterHoursEnabled,
            afterHoursMultiplier,
        });
        return result.totalCost;
    }
    if (section.showQty && subItem.qty != null && subItem.qty !== "") {
        const qtyNum = parseFloat(String(subItem.qty).replace(/[^0-9.-]/g, "")) || 0;
        return Math.round(unitCostNum * qtyNum * 100) / 100;
    }
    if (unitCostNum > 0) {
        return Math.round(unitCostNum * 100) / 100;
    }
    return parseCurrency(subItem.totalCost) || 0;
}
function costSubItemToEstimating(item) {
    return {
        id: item.id,
        title: item.title,
        inScope: false,
        unitCost: item.rate != null ? String(item.rate) : "",
        totalCost: "",
        notes: "",
        selectedUnit: item.selectedUnit || "",
    };
}
function costSectionToEstimating(section) {
    return {
        sectionTitle: section.sectionTitle,
        sectionId: section.sectionId,
        showQty: section.showQty,
        items: section.items.map(costSubItemToEstimating),
    };
}
function costItemToEstimating(item) {
    return {
        id: item.id,
        title: item.title,
        sections: item.sections?.map(costSectionToEstimating),
    };
}
/** Default group6 accordion in estimating shape (inScope, unitCost, totalCost, notes). */
export function getDefaultGroup6AccordionData() {
    const full = getDefaultCostData();
    return full.group6.map(costItemToEstimating);
}
/** Merge group6 sections from API (costsService.getGroups()) into estimating accordion.
 *
 * For "other" sections specifically: fully replaces placeholder items with API items
 * so any label/rate changes in Cost Settings are reflected immediately. The user-managed
 * "other_custom" section is never touched by this function.
 *
 * For all other categories: updates rates/units for existing matching items only.
 */
export function mergeGroup6FromApi(accordion, groups) {
    const group6 = groups.find((g) => g.id === "group6");
    if (!group6)
        return accordion;
    const next = accordion.map((item) => ({
        ...item,
        sections: item.sections?.map((sec) => ({
            ...sec,
            items: sec.items.map((i) => ({ ...i })),
        })),
    }));
    const otherAccItem = next.find((a) => a.id === "other");
    for (const section of group6.sections) {
        const category = getGroup6CategoryFromSectionId(section.id);
        if (!category)
            continue;
        const localSectionId = stripGroup6SectionIdPrefix(section.id, category);
        if (category === "other") {
            if (!otherAccItem)
                continue;
            // Never overwrite manually-added items managed by the user
            if (localSectionId === "other_custom")
                continue;
            const existingSection = otherAccItem.sections?.find((s) => s.sectionId === localSectionId);
            const apiMappedItems = section.items.map((apiItem) => ({
                id: apiItem.id,
                title: apiItem.label,
                inScope: false,
                unitCost: apiItem.rate != null ? String(apiItem.rate) : "",
                totalCost: "",
                notes: "",
                selectedUnit: apiItem.selectedUnit ?? "",
            }));
            if (existingSection) {
                // Replace all items — cost settings are the source of truth here
                existingSection.items = apiMappedItems;
            }
            else if (apiMappedItems.length > 0) {
                // API introduced a new section not in defaults — append it
                if (!otherAccItem.sections)
                    otherAccItem.sections = [];
                otherAccItem.sections.push({
                    sectionId: localSectionId,
                    sectionTitle: section.title || undefined,
                    items: apiMappedItems,
                });
            }
        }
        else {
            // For flooring_additional and plastering: update rates/units for existing items only
            for (const accItem of next) {
                const matchingSection = accItem.sections?.find((s) => s.sectionId === localSectionId);
                if (!matchingSection)
                    continue;
                for (const apiItem of section.items) {
                    const localItem = matchingSection.items.find((i) => i.id === apiItem.id);
                    if (localItem) {
                        localItem.unitCost =
                            apiItem.rate != null ? String(apiItem.rate) : localItem.unitCost;
                        localItem.selectedUnit =
                            apiItem.selectedUnit ?? localItem.selectedUnit;
                    }
                }
            }
        }
    }
    return next;
}
/** Apply latest cost parameters (rates/units) to group6 accordion. Preserves inScope, qty, notes. */
export function applyGroup6CostParametersToAccordion(accordion, groups) {
    const group6 = groups.find((g) => g.id === "group6");
    if (!group6)
        return accordion;
    const next = accordion.map((item) => ({
        ...item,
        sections: item.sections?.map((sec) => ({
            ...sec,
            items: sec.items.map((i) => ({ ...i })),
        })),
    }));
    const otherAccItem = next.find((a) => a.id === "other");
    for (const section of group6.sections) {
        const category = getGroup6CategoryFromSectionId(section.id);
        if (!category)
            continue;
        const localSectionId = stripGroup6SectionIdPrefix(section.id, category);
        if (category === "other") {
            if (!otherAccItem || localSectionId === "other_custom")
                continue;
            const existingSection = otherAccItem.sections?.find((s) => s.sectionId === localSectionId);
            if (!existingSection)
                continue;
            for (const apiItem of section.items) {
                const localItem = existingSection.items.find((i) => i.id === apiItem.id);
                if (localItem) {
                    localItem.unitCost =
                        apiItem.rate != null ? String(apiItem.rate) : localItem.unitCost;
                    localItem.selectedUnit =
                        apiItem.selectedUnit ?? localItem.selectedUnit;
                }
            }
        }
        else {
            for (const accItem of next) {
                const matchingSection = accItem.sections?.find((s) => s.sectionId === localSectionId);
                if (!matchingSection)
                    continue;
                for (const apiItem of section.items) {
                    const localItem = matchingSection.items.find((i) => i.id === apiItem.id);
                    if (localItem) {
                        localItem.unitCost =
                            apiItem.rate != null ? String(apiItem.rate) : localItem.unitCost;
                        localItem.selectedUnit =
                            apiItem.selectedUnit ?? localItem.selectedUnit;
                    }
                }
            }
        }
    }
    return next;
}
/** Build lineItems for create/update from group6 accordion. Prefix sectionIds for persistence. */
export function collectGroup6LineItems(accordion) {
    const workItems = [];
    let sortOrder = 0;
    const parseNum = (v) => {
        if (v == null || v === "")
            return undefined;
        const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : undefined;
    };
    const categoryFromItemId = (itemId) => {
        if (itemId === "floor-finishes" ||
            itemId === "screeds" ||
            itemId === "grinding")
            return "flooring_additional";
        if (itemId === "plastering")
            return "plastering";
        if (itemId === "other")
            return "other";
        return "flooring_additional";
    };
    accordion.forEach((accordionItem) => {
        const prefix = getGroup6SectionIdPrefixForAccordionItem(accordionItem.id);
        const category = categoryFromItemId(accordionItem.id);
        accordionItem.sections?.forEach((section) => {
            const prefixedSectionId = prefix + section.sectionId;
            section.items.forEach((subItem) => {
                workItems.push({
                    category,
                    sectionId: prefixedSectionId,
                    sectionTitle: section.sectionTitle,
                    parentTitle: accordionItem.title,
                    itemId: subItem.id,
                    itemTitle: subItem.title,
                    inScope: subItem.inScope,
                    quantity: subItem.qty ? parseNum(subItem.qty) : undefined,
                    unitCost: parseNum(subItem.unitCost),
                    totalCost: parseNum(subItem.totalCost),
                    notes: subItem.notes || undefined,
                    selectedUnit: subItem.selectedUnit,
                    sortOrder: sortOrder++,
                });
            });
        });
    });
    return workItems;
}
/** Merge saved quote lineItems (with prefixed sectionIds) into group6 accordion. Creates missing sections/items for custom Other. */
export function mergeLineItemsToGroup6Accordion(accordion, lineItems) {
    if (!lineItems?.length)
        return accordion;
    const next = accordion.map((item) => ({
        ...item,
        sections: item.sections?.map((s) => ({
            ...s,
            items: s.items.map((i) => ({ ...i })),
        })),
    }));
    const applyToSubItem = (subItem, li) => {
        subItem.inScope = li.inScope ?? subItem.inScope;
        subItem.qty = li.quantity != null ? String(li.quantity) : subItem.qty;
        subItem.unitCost =
            li.unitCost != null ? String(li.unitCost) : subItem.unitCost;
        subItem.totalCost =
            li.totalCost != null ? String(li.totalCost) : subItem.totalCost;
        subItem.notes = li.notes ?? subItem.notes;
        subItem.selectedUnit =
            li.selectedUnit ?? li.selectedOption ?? subItem.selectedUnit;
    };
    lineItems.forEach((li) => {
        const category = li.category;
        if (!category)
            return;
        const prefixedSectionId = li.sectionId || "";
        const localSectionId = getGroup6CategoryFromSectionId(prefixedSectionId)
            ? stripGroup6SectionIdPrefix(prefixedSectionId, category)
            : prefixedSectionId;
        const itemId = li.itemId || "";
        const itemTitle = li.itemTitle || "";
        // Both "plastering" and "other" use sectionId "placeholder" - must resolve by category
        let accItem;
        if (category === "other") {
            accItem = next.find((a) => a.id === "other");
        }
        else if (category === "plastering") {
            accItem = next.find((a) => a.id === "plastering");
        }
        else {
            accItem = next.find((a) => a.sections?.some((s) => s.sectionId === localSectionId));
        }
        if (!accItem && next.length > 0)
            accItem = next[0];
        if (!accItem)
            return;
        let section = accItem.sections?.find((s) => s.sectionId === localSectionId);
        if (!section) {
            section = {
                sectionId: localSectionId,
                sectionTitle: localSectionId === "other_custom"
                    ? "Manual Entries"
                    : li.sectionTitle,
                showQty: localSectionId === "other_custom" ? true : undefined,
                items: [],
            };
            accItem.sections = [...(accItem.sections || []), section];
        }
        // Match by itemId first; for "other" sections fall back to itemTitle to survive ID changes in Cost Settings
        let subItem = section.items.find((i) => i.id === itemId);
        if (!subItem && category === "other" && itemTitle) {
            subItem = section.items.find((i) => i.title.toLowerCase() === itemTitle.toLowerCase());
        }
        if (!subItem) {
            section.items = [
                ...section.items,
                {
                    id: itemId,
                    title: itemTitle,
                    inScope: li.inScope ?? false,
                    qty: li.quantity != null ? String(li.quantity) : undefined,
                    unitCost: li.unitCost != null ? String(li.unitCost) : "",
                    totalCost: li.totalCost != null ? String(li.totalCost) : "",
                    notes: li.notes || "",
                    selectedUnit: li.selectedUnit ?? li.selectedOption ?? "",
                },
            ];
        }
        else {
            applyToSubItem(subItem, li);
        }
    });
    return next;
}
export { getGroup6SectionIdPrefixForAccordionItem, getGroup6CategoryFromSectionId, stripGroup6SectionIdPrefix, } from "@/features/admin/cost-parameters/costParametersDefaults";
